import { createFileRoute, Link } from "@tanstack/react-router";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/en/services/conversion-rate-optimization")({
  head: () => {
    const h = pageHead("conversionRateOptimization", "en");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Conversion Rate Optimization Agency",
          description:
            "CRO agency with research discipline: quantitative funnel analysis, qualitative insights, statistically clean tests. For e-commerce, B2B, and SaaS.",
          url: "/en/services/conversion-rate-optimization",
          serviceType: "ConversionRateOptimization",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Home", path: "/en" },
          { name: "Services", path: "/en/services" },
          { name: "Conversion Rate Optimization", path: "/en/services/conversion-rate-optimization" },
        ])),
      ],
    };
  },
  component: CROPage,
});

const painPoints = [
  {
    title: `"Let's just test something"`,
    body: `Without a hypothesis and without a research foundation, a test is worthless — even if it delivers a result. What was learned? Why did it work? Can the result be transferred to other areas? Tests without hypothesis are gambling with ad budget. We start every test with a documented hypothesis: "We suspect that X happens because Y, measured via Z."`,
  },
  {
    title: `Sample size isn't calculated`,
    body: `A test with too small a sample is statistically worthless. "Variant B is 12% better after 100 conversions" isn't a result, it's chance. A serious CRO practice calculates the necessary sample size before every test based on baseline conversion rate, minimum detectable effect, and desired confidence level (typically 95%). Tests without this calculation are theater.`,
  },
  {
    title: `Optimizing for micro KPIs instead of profitability`,
    body: `Click-through rate on a banner increased by 30% — and revenue stays the same. Add-to-cart rate improved — but checkout conversion worsened. Optimizing on individual metrics without funnel validation leads to shifts, not profitability. We always measure on the end-conversion value, not on intermediate steps.`,
  },
  {
    title: `Test pipeline without prioritization`,
    body: `"Let's test the headline first. Then the image. Then the button." Without prioritization by economic leverage, 80% of test capacity is wasted on 20% of impact potential. We prioritize with ICE or PIE frameworks: Impact, Confidence, Ease — weighted by mandate reality.`,
  },
];

const triade = [
  {
    eyebrow: "Quantitative",
    title: "The WHERE",
    body: "Funnel analysis via GA4 and BigQuery. Heatmaps and click tracking via Microsoft Clarity or Hotjar. Cohort analysis for return behavior. Drop-off analyses per funnel stage. Output: concrete hotspots where money is being lost economically.",
  },
  {
    eyebrow: "Qualitative",
    title: "The WHY",
    body: "Session recordings of selected user sessions. Customer surveys at strategic funnel points. User tests with small sample. Heuristic analysis according to Nielsen, Krug, or other established UX frameworks. Output: hypotheses why the hotspots exist.",
  },
  {
    eyebrow: "Statistical",
    title: "The WHAT",
    body: "Methodology appropriate to volume: A/B tests with correctly calculated sample size where volume permits. Bayesian statistics at medium volume for more valid statements with less data. At small volume: heuristic-based validation plus documented best-practice implementation instead of unreliable tests. Output: volume-appropriate, validated improvements.",
  },
];

const pipeline = [
  { num: "01", title: "Funnel Diagnosis (3–5 days)", body: "Quantitative analysis of the existing funnel. GA4 audit, tracking validation, drop-off identification per funnel stage. Output: prioritized hotspot list with economic impact potential." },
  { num: "02", title: "Research & Hypothesis Formation (1–2 weeks)", body: "Qualitative insights via heatmaps, session recordings, optional customer surveys. Heuristic analysis according to UX frameworks. At least three documented hypotheses per hotspot with concrete measurement definition." },
  { num: "03", title: "Test Pipeline Setup (1 week)", body: "ICE or PIE prioritization of hypotheses. Sample size calculation per test. Test setup in VWO, Optimizely, or comparable platform. Tracking validation before go-live." },
  { num: "04", title: "Test Execution (continuous, from week 3–4)", body: `Weekly test cycle with clear hypothesis pipeline. Statistical significance check before test ending. Sequential testing where appropriate for shorter test durations. Strictly no cherry-picking, no premature ending at "good interim states."` },
  { num: "05", title: "Implementation & Scaling (parallel to Phase 04)", body: `Successful tests are rolled out to production. Insights flow into subsequent hypothesis formation. Building a documented "lessons learned" database per mandate.` },
  { num: "06", title: "Customer Lifecycle Extension (from month 3)", body: `Tests are extended across the entire funnel — ad click, landing page, product detail, cart, checkout, post-purchase, retention. CRO isn't a "landing page only" game, but customer journey optimization.` },
];

const capabilities = [
  { name: "Funnel Diagnosis & Hotspot Mapping", detail: "GA4, BigQuery, drop-off analysis" },
  { name: "Heuristic & Heatmap Analysis", detail: "Nielsen, Krug, Clarity, Hotjar" },
  { name: "A/B & Multivariate Testing", detail: "Sample size, Bayesian, sequential" },
  { name: "Customer Lifecycle Testing", detail: "Ad click to retention" },
  { name: "UX Research & Customer Insights", detail: "Surveys, user tests, VoC" },
  { name: "Tracking Hygiene as Prerequisite", detail: "Server-side, Enhanced Conversions" },
  { name: "Economic Validation", detail: "End-conversion value, contribution margin" },
  { name: "Test Documentation & Lessons Learned", detail: "Knowledge accumulation" },
];

const volumeClasses = [
  {
    eyebrow: "CLASS 1 — SMALL VOLUME",
    badge: "CALVARIUS FOCUS",
    title: "200–1,000 monthly conversions",
    body: "This is where most SME setups we manage are located. We work primarily with qualitative methods: customer research, user tests with small samples, heuristic analysis according to UX frameworks, heatmap diagnosis via Microsoft Clarity or Hotjar. We deploy A/B tests selectively where we expect substantial effects (15–20% or more) and where test periods of 4–8 weeks are practical. Realistic improvements: 8–25% within 6–9 months — primarily through clean research, best-practice implementation, and targeted tests, not through test-pipeline volume.",
  },
  {
    eyebrow: "CLASS 2 — MEDIUM VOLUME",
    badge: "COMMON CALVARIUS AREA",
    title: "1,000–5,000 monthly conversions",
    body: "Here classic A/B testing becomes economically viable — but with adjustments compared to enterprise methodology. We use Bayesian statistics instead of frequentist (gets to valid statements with less data), sequential testing for shorter test durations, clear focus on large expected effects instead of fine optimization. Realistic improvements: 10–30% within 6 months, with 2–4 parallel running tests.",
  },
  {
    eyebrow: "CLASS 3 — VERY SMALL VOLUME",
    badge: null,
    title: "Under 200 monthly conversions",
    body: "Here classic A/B tests aren't statistically valid — not even with Bayesian statistics. We then work exclusively qualitatively: heuristic analysis, customer surveys, user tests, best-practice refactoring based on documented industry standards. We say this honestly before the mandate — not that we reject mandates, but that we adapt the methodology to reality. Measurable effects in 4–8 weeks.",
  },
  {
    eyebrow: "CLASS 4 — HIGH VOLUME",
    badge: null,
    title: "5,000+ monthly conversions",
    body: "Enterprise area where classic A/B testing with 95% confidence level is fully scalable. Multivariate tests possible. Test durations of 2–4 weeks per test. Test pipeline with 3–5 parallel running tests. Calvarius also manages mandates of this size — but it isn't the focus of our mandate mix, but another volume class we cover with methodological rigor.",
  },
];

const benefits = [
  { strong: "Research substance before every action.", body: "We don't test blindly — every CRO action starts with a documented hypothesis based on quantitative and qualitative research." },
  { strong: "Statistical discipline as mandatory component.", body: "Sample size calculation, correct confidence levels, no cherry-picking, no premature ending. Real scientific rigor, not test theater." },
  { strong: "Economic grounding instead of micro KPI optimization.", body: "We measure on end-conversion value and contribution margin, not on click-through rate or add-to-cart rate." },
  { strong: "Customer lifecycle view instead of landing page tunnel.", body: "CRO across the entire funnel, because impact arises where friction sits — and friction rarely sits only on a landing page." },
  { strong: "Honest volume realism.", body: "We tell you before the mandate whether CRO is economically viable at your traffic volume. If not: other levers instead of CRO." },
  { strong: "Knowledge accumulation per mandate.", body: "Test documentation and lessons-learned database — insights from earlier tests flow into later hypotheses." },
];

function CROPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Services · Conversion Rate Optimization"
          headline={<>Conversion Rate Optimization Agency — <em>Hypothesis Discipline,</em> Not A/B Test Theater.</>}
          lead="Quantitative data, qualitative insights, and statistically clean validation — as a coherent system, not push-button optimization. Calvarius works as a CRO agency for e-commerce, B2B lead generation, and SaaS trial sign-ups, with clear research discipline before every action."
          metaStrip={["Research before testing", "Statistical discipline", "Economically grounded"]}
          breadcrumbs={[{ label: "Home", to: "/en" }, { label: "Services", to: "/en/services" }, { label: "Conversion Rate Optimization" }]}
        />

        <Section number="02" kicker="Standpoint" headline={<>Research discipline <em>before</em> action.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">{`Anyone looking for a conversion rate optimization agency has typically made a critical observation: the market is full of providers selling A/B tests without understanding the discipline behind them. "Let's see if green converts better than red" isn't CRO — it's gambling with ad budget. A test without a hypothesis is a test without learning effect. A test without sufficient sample becomes a random result. A test without economic grounding optimizes for vanity metrics instead of contribution margin. Anyone taking CRO seriously works differently.`}</p>
              <p className="mb-5">Calvarius treats conversion rate optimization as a research discipline with action sequence — not as a push-button test workshop. That means: before every test stands a hypothesis. Before every hypothesis stands research. Before the research stands a clear funnel diagnosis. We know where in the funnel the economically relevant conversion losses happen — and prioritize where the economic leverage is greatest. A 5% improvement on a product detail page with high margin is worth more than a 30% improvement on a newsletter signup.</p>
              <p className="mb-5">The methodological triad we work with isn't negotiable: quantitative funnel analysis shows <strong>where</strong> problems are. Qualitative insights via heatmaps, session recordings, and customer research show <strong>why</strong> problems arise. A/B tests with correct statistics validate <strong>what</strong> works. Anyone leaving out one of these three levels doesn't practice CRO — but gut feeling with a test coating. We use all three systematically and in the right order.</p>
              <p className="mb-5">Concretely, that means: we work with platform tools like Microsoft Clarity, Hotjar, VWO, and Optimizely — combined with GA4 and BigQuery export for a clean data foundation. We test across the entire customer lifecycle, not just on individual landing pages — ad click, landing page, product detail, cart, checkout, post-purchase, retention. And we choose the methodology appropriate to volume: For SME setups with 200–1,000 monthly conversions, we work primarily qualitatively — customer research, heuristic analysis, targeted tests for large expected effects. For medium volume with Bayesian statistics and sequential testing. For high volume with classic A/B testing setup. Anyone selling SMEs enterprise methodology burns their budget.</p>
              <p>For Shopify stores, we always look at conversion in interplay with the technical base — theme performance, checkout customization, and app selection co-determine how much a test can actually lift. More on our <Link to="/en/services/ecommerce-development/shopify-agency" className="text-cyan-deep underline-offset-4 hover:underline">Shopify agency</Link> page.</p>
            </div>
          </RevealOnScroll>
          <Callout>A test without a hypothesis is gambling with ad budget. We don't test blindly.</Callout>
        </Section>

        <Section variant="tinted" number="03" kicker="Typical failures" headline={<>Where CRO <em>systematically fails</em> in practice.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {painPoints.map((p, i) => (
                <article key={i} className="bg-white p-7">
                  <div className="font-mono text-xs tracking-wider text-cyan-deep">FAILURE 0{i + 1}</div>
                  <h3 className="mt-3 font-display text-xl text-ink">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{p.body}</p>
                </article>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Our methodology" headline={<>Quantitative, qualitative, statistical — as a <em>coherent</em> system.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {triade.map((col, i) => (
                <div key={i} className="bg-white p-7">
                  <p className="eyebrow mb-3">{col.eyebrow}</p>
                  <h3 className="font-display text-xl text-ink">{col.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{col.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
          <RevealOnScroll>
            <p className="mt-10 max-w-2xl text-[17px] leading-relaxed text-ink-2">
              This triad isn't modular — all three layers are mandatory. Anyone testing without research optimizes blindly. Anyone only looking at heatmaps without validation optimizes anecdotally. Anyone analyzing only funnel data without qualitative depth sees the where, but not the why. Only the combination delivers systematic improvement.
            </p>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="Our approach" headline={<>How a CRO mandate <em>runs with us.</em></>}>
          <ProcessFlow steps={pipeline} />
          <RevealOnScroll>
            <p className="mt-10 max-w-2xl text-[17px] leading-relaxed text-ink-2">
              This pipeline doesn't run linearly but cyclically — every test insight flows back into hypothesis formation. Economic leverage grows with every iteration because knowledge about the specific mandate audience grows.
            </p>
          </RevealOnScroll>
        </Section>

        <Section number="06" kicker="What we deliver" headline={<>Capabilities across the <em>CRO spectrum.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section variant="tinted" number="07" kicker="Tooling" headline={<>What we manage CRO <em>operationally</em> with.</>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              {[
                { title: "Quantitative data foundation", items: ["Google Analytics 4 with BigQuery export", "Server-side tracking via GTM Server", "Funnel visualization in Looker Studio"] },
                { title: "Qualitative insights", items: ["Microsoft Clarity (free, GDPR-compliant)", "Hotjar (for deeper heatmap and survey features)", "Custom survey tools depending on requirements"] },
                { title: "A/B and multivariate testing", items: ["VWO — main platform for most mandates", "Optimizely — where enterprise features or server-side testing are needed", "Convert.com as mid-market alternative", "Own implementations via feature flags where platform tools aren't enough"] },
                { title: "Statistical tools", items: ["Sample size calculator (internal, Calvarius-owned spreadsheets)", "Bayesian statistics toolkit for more complex analyses", "Sequential testing frameworks for shorter test durations"] },
              ].map((b) => (
                <div key={b.title} className="rounded-lg border border-rule bg-white p-6">
                  <p className="eyebrow mb-3">{b.title}</p>
                  <ul className="space-y-2 text-sm leading-relaxed text-ink-2">
                    {b.items.map((it) => (
                      <li key={it} className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>{it}</span></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="08"
          kicker="What's realistic"
          headline={<>Volume realism — the right methodology for <em>your setup.</em></>}
          caption="CRO isn't just classic A/B testing. Most Calvarius mandates are SMEs from the e-commerce sector with monthly budgets of €10,000–100,000 — and thus setups where enterprise A/B test methodology isn't economically viable. We work with the methodology that fits the volume class — not the one that sounds most impressive in conference talks."
        >
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {volumeClasses.map((c, i) => (
                <article key={i} className="bg-white p-7">
                  <div className="font-mono text-xs tracking-wider text-cyan-deep">{c.eyebrow}</div>
                  <h3 className="mt-3 font-display text-xl text-ink">{c.title}</h3>
                  {c.badge && (
                    <p className="mt-2 font-mono text-[11px] tracking-wider text-ink-4">{c.badge}</p>
                  )}
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{c.body}</p>
                </article>
              ))}
            </div>
          </RevealOnScroll>
          <RevealOnScroll>
            <p className="mt-10 max-w-2xl text-[17px] leading-relaxed text-ink-2">
              In the Calvarius mandate reality, most setups fall into Class 1 or 2 — SME setups with B2C e-commerce focus, where choosing the right CRO methodology is economically decisive. Anyone selling SMEs enterprise methodology burns their budget. Anyone offering SMEs nothing because volume isn't sufficient for classic testing leaves economic leverage on the table. We do neither — we deliver the methodology that fits the setup.
            </p>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="09" kicker="What you gain" headline={<>What you get from our <em>CRO discipline.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white p-6">
                  <p className="text-[15px] leading-relaxed text-ink-2"><strong className="text-ink">{b.strong}</strong> {b.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <LogoReferences locale="en" number="10" />

        <RelatedServices
          locale="en"
          fromId="conversion-rate-optimierung"
          number="11"
          items={relatedBySource["conversion-rate-optimierung"]}
        />

        <Section number="12" kicker="Get concrete">
          <CTABlock
            headline={<>CRO audit in 30 minutes — <em>free</em> and non-binding.</>}
            body="In a first conversation, we clarify whether your setup is economically CRO-viable — i.e., whether traffic volume is sufficient for valid tests and where the most impactful hotspots are. You get an honest assessment, even if that means CRO isn't the right lever right now."
            primary={{ label: "Schedule a conversation", to: "/en/contact?topic=cro" }}
            secondary={{ label: "All services", to: "/en/services" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
